import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const kungsbackaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-kungsbacka",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Kungsbacka – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Kungsbacka. Vi hämtar möbler, vitvaror och skräp i Kungsbacka, Onsala, Särö, Kullavik, Fjärås och Åsa. Fast pris, ofta samma dag.",
      h1: "Bortforsling Kungsbacka",
    },

    hero: {
      heading: "Bortforsling i Kungsbacka",
      intro:
        "Boka bortforsling i Kungsbacka – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Aranäs och centrala Kungsbacka till villorna på Onsala, Särö och Kullavik. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Kungsbacka – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Kungsbacka",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Kungsbacka och körs till Klovsten återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa på Onsala, Kungsbacka",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i centrala Kungsbacka",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Aranäs, Kungsbacka",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Särö, Kungsbacka",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Kullavik, Kungsbacka",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Kungsbacka – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Kungsbacka är en stor kustkommun i norra Halland och en del av Göteborgsregionen, med pendlingsavstånd till Göteborg på drygt 20 minuter. Bebyggelsen är varierad: lägenhetskvarteren i nya Aranäs och centrala Kungsbacka med sin gamla trästad, de havsnära villaområdena på Onsalahalvön, Särö, Kullavik och Vallda, samt inlandets villor och byar i Fjärås, Anneberg och Hällingsjö. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det i kustvillorna snarare handlar om stora tomter, garage och uthus. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Kungsbacka har en stor fördel här – kommunen har tre återvinningscentraler: Klovsten i centrala Kungsbacka, Barnamossen samt Frillesås i söder. Det gör att det nästan alltid finns en station nära din adress. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E6 går rakt genom Kungsbacka och Västkustbanans pendeltåg stannar vid Hede, Kungsbacka, Anneberg och Åsa tar vi oss snabbt mellan orterna, och vi når kusten smidigt via Säröleden. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både lägenhetskvarteren i centrum och de stora villatomterna ute på Onsala och Särö – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Kungsbacka",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa på Onsala, Särö, Kullavik eller Vallda är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Aranäs eller centrala Kungsbacka, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller uthuset. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Kungsbackabo på Klovsten, Barnamossen eller Frillesås återvinningscentral.",
      additionalSections: [
        {
          heading: "Orter och områden vi täcker i Kungsbacka",
          content:
            "Vi utför bortforsling i hela Kungsbacka kommun – från de centrala lägenhetsområdena till de havsnära villakvarteren och inlandets byar. Oavsett om du bor i en lägenhet i Aranäs, en villa på Onsala, ett hus i Särö eller på landet i Fjärås kan vi vara på plats, ofta redan samma dag.\n\nI centrala Kungsbacka – med Aranäs, Kolla, Tölö och den gamla trästaden kring torget – har vi stor vana vid både lägenhetshämtningar med hiss och trapphus och de smala gatorna i den historiska bebyggelsen. I de kustnära områdena Onsala, Särö, Kullavik och Vallda möter vi stora villatomter, garage och uthus.\n\nVi är minst lika snabba i kommunens övriga delar: Åsa och Frillesås i söder längs kusten, samt Fjärås, Anneberg, Gällinge och Hällingsjö i inlandet. Tack vare närheten till E6, Västkustbanan och Säröleden planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon ort.",
        },
        {
          heading: "Återvinning och miljöansvar i Kungsbacka",
          content:
            "En viktig del av vår bortforsling i Kungsbacka är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Kungsbacka har en stor fördel eftersom kommunen har tre stationer på olika sidor av kommunen:\n\nKlovsten återvinningscentral – huvudstationen i centrala Kungsbacka nära E6, där sorterat grovavfall, elavfall, förpackningar och farligt avfall tas emot, liksom föremål för återbruk. Barnamossen återvinningscentral – med bland annat flisning av trädgårdsavfall. Frillesås återvinningscentral – för uppdrag i de södra delarna. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Kungsbacka med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Kungsbackas orter, från lägenhetskvarteren i Aranäs och centrum till villatomterna på Onsala och Särö, och vet att kommunens tre återvinningscentraler ligger spridda nära dig. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och E6 går rakt genom Kungsbacka. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon ort i Kungsbacka, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Klovsten, Barnamossen och Frillesås återvinningscentral. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Kungsbacka?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon ort i Kungsbacka.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Kungsbacka?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Kungsbacka?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och E6 går rakt genom Kungsbacka, så vi tar oss snabbt mellan orterna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Kungsbacka?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Kungsbacka?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen i Aranäs och centrala Kungsbacka. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa, garage och uthus i Kungsbacka?",
        answer:
          "Ja, en stor del av våra uppdrag i Kungsbacka är villahämtningar på Onsala, Särö, Kullavik och Vallda. Vi backar in på uppfarten där det går och tömmer garage, källare, uthus och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Kungsbacka försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Kungsbacka?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Kungsbacka har tre stationer – Klovsten, Barnamossen och Frillesås – så det finns alltid en nära din adress. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Kungsbacka på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Kungsbacka?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen, uthus och garage. Vi är vana vid både villaförråden på Onsala och Särö och lägenhetsförråden i Aranäs.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Kungsbacka?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i centrala Kungsbacka och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka orter i Kungsbacka täcker ni?",
        answer:
          "Vi utför bortforsling i hela Kungsbacka – centrala Kungsbacka (Aranäs, Kolla, Tölö), Onsala, Särö, Kullavik, Vallda, Åsa, Frillesås, Fjärås, Anneberg, Gällinge och Hällingsjö. Inga extra avgifter för någon ort.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Kungsbacka?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Kungsbackabo på Klovsten, Barnamossen eller Frillesås återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Kungsbacka utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Kungsbacka i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Kungsbacka kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Kungsbacka idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Kungsbacka kommun.",
    },

    en: {
      slug: "junk-removal-kungsbacka",
      meta: {
        title: "Junk Removal Kungsbacka – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Kungsbacka. We pick up furniture, appliances and bulky items in Kungsbacka, Onsala, Särö, Kullavik, Fjärås and Åsa. Fixed price, often same day.",
        h1: "Junk Removal Kungsbacka",
      },
      hero: {
        heading: "Junk Removal in Kungsbacka",
        intro:
          "Book junk removal in Kungsbacka – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Aranäs and central Kungsbacka to the villas on Onsala, Särö and Kullavik. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Kungsbacka – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Kungsbacka",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Kungsbacka and driven to Klovsten recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa on Onsala, Kungsbacka",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in central Kungsbacka",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Aranäs, Kungsbacka",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Särö, Kungsbacka",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Kullavik, Kungsbacka",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Kungsbacka – fast, hassle-free and responsible",
        mainText:
          "Kungsbacka is a large coastal municipality in northern Halland and part of the Gothenburg region, within a 20-minute commute of Gothenburg. The housing is varied: the apartment quarters of the new Aranäs district and central Kungsbacka with its old wooden town, the seaside villa areas of the Onsala peninsula, Särö, Kullavik and Vallda, and the inland villas and villages of Fjärås, Anneberg and Hällingsjö. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while in the coastal villas it is more about large plots, garages and outbuildings. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Kungsbacka has a big advantage here – the municipality has three recycling centres: Klovsten in central Kungsbacka, Barnamossen, and Frillesås in the south. This means there is almost always a station near your address. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E6 runs straight through Kungsbacka and the Västkustbanan commuter train stops at Hede, Kungsbacka, Anneberg and Åsa, we move quickly between the towns, and we reach the coast smoothly via Säröleden. This means we can often be on site the same day you book. Our workers are used to both the apartment quarters of the centre and the large villa plots out on Onsala and Särö – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Kungsbacka",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa on Onsala, Särö, Kullavik or Vallda, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Aranäs or central Kungsbacka, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or outbuilding. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Kungsbacka resident you can drop those off for free at the Klovsten, Barnamossen or Frillesås recycling centre.",
        additionalSections: [
          {
            heading: "Towns and areas we cover in Kungsbacka",
            content:
              "We perform junk removal across the entire municipality of Kungsbacka – from the central apartment areas to the seaside villa neighbourhoods and the inland villages. Whether you live in an apartment in Aranäs, a villa on Onsala, a house in Särö or in the countryside in Fjärås, we can be on site, often the same day.\n\nIn central Kungsbacka – with Aranäs, Kolla, Tölö and the old wooden town around the square – we are highly experienced with both apartment pickups via elevator and stairwell and the narrow streets of the historic district. In the coastal areas of Onsala, Särö, Kullavik and Vallda we meet large villa plots, garages and outbuildings.\n\nWe are just as quick in the other parts of the municipality: Åsa and Frillesås to the south along the coast, and Fjärås, Anneberg, Gällinge and Hällingsjö inland. Thanks to the proximity of the E6, the Västkustbanan and Säröleden, we always plan the route in advance so you don't have to wait. No extra charges for any town.",
          },
          {
            heading: "Recycling and environmental responsibility in Kungsbacka",
            content:
              "An important part of our junk removal in Kungsbacka is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Kungsbacka has a big advantage because the municipality has three stations on different sides of the municipality:\n\nKlovsten recycling centre – the main station in central Kungsbacka near the E6, which receives sorted bulky waste, e-waste, packaging and hazardous waste, as well as items for reuse. Barnamossen recycling centre – which includes chipping of garden waste. Frillesås recycling centre – for jobs in the southern parts. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Kungsbacka with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Kungsbacka's towns, from the apartment quarters of Aranäs and the centre to the villa plots on Onsala and Särö, and that the municipality's three recycling centres are spread out close to you. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and the E6 runs straight through Kungsbacka. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any town in Kungsbacka, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the Klovsten, Barnamossen and Frillesås recycling centres. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Kungsbacka cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any town in Kungsbacka.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Kungsbacka?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Kungsbacka?",
          answer:
            "We have vans and staff across the Gothenburg region and the E6 runs straight through Kungsbacka, so we move quickly between the towns. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Kungsbacka?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Kungsbacka?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings in Aranäs and central Kungsbacka. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas, garages and outbuildings in Kungsbacka?",
          answer:
            "Yes, a large share of our jobs in Kungsbacka are villa pickups on Onsala, Särö, Kullavik and Vallda. We reverse onto the driveway where possible and clear garages, basements, outbuildings and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Kungsbacka insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Kungsbacka?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Kungsbacka has three stations – Klovsten, Barnamossen and Frillesås – so there is always one near your address. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Kungsbacka on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Kungsbacka?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics, outbuildings and garages. We are used to both the villa storage rooms on Onsala and Särö and the apartment storage units in Aranäs.",
        },
        {
          question: "Can you make multiple stops during junk removal in Kungsbacka?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in central Kungsbacka and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which towns in Kungsbacka do you cover?",
          answer:
            "We perform junk removal across all of Kungsbacka – central Kungsbacka (Aranäs, Kolla, Tölö), Onsala, Särö, Kullavik, Vallda, Åsa, Frillesås, Fjärås, Anneberg, Gällinge and Hällingsjö. No extra charges for any town.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Kungsbacka?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Kungsbacka resident you can drop those off for free at the Klovsten, Barnamossen or Frillesås recycling centre.",
        },
        {
          question: "Which municipalities around Kungsbacka do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Kungsbacka in 3 easy steps",
        step2Text:
          "Our young workers in Kungsbacka come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Kungsbacka today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Kungsbacka.",
      },
    },
  },
];
